const axios = require('axios');
const Promise = require('bluebird');
const { sortedUniqBy } = require('lodash');
const queryString = require('query-string');

const BASE_URL = 'https://api.cognitedata.com';

class CogniteSDKError extends Error {
  constructor(message, status) {
    super(message);
    this.name = this.constructor.name;
    this.status = status;
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, this.constructor);
    } else {
      this.stack = new Error(message).stack;
    }
  }
}

const instance = axios.create({
  baseUrl: BASE_URL,
  headers: {},
});

const window = global.window || {
  location: {
    href: '',
  },
};

const initialOptions = {
  baseUrl: BASE_URL,
  project: null,
  version: '0.5',
  apiKey: null,
  withCredentials: false,
};

let options = Object.assign({}, initialOptions);

const apiUrl = () => `api/${options.version}/`;

const retrieveData = response => {
  // At the top level, we have the headers; save them so that we can return them
  // as a sidecar to the `data` object.
  const { headers } = response;
  // Not everything is result.data.data, so find the deepest one.
  let current = response;
  while (current.data) {
    current = current.data;
  }
  if (
    current &&
    typeof current === 'object' &&
    current.constructor === Object
  ) {
    current.headers = headers;
  }
  return current;
};

function rawGet(url, params) {
  return instance({
    method: 'get',
    url: `${options.baseUrl}/${url}`,
    withCredentials: options.withCredentials,
    params,
  }).then(retrieveData);
}

const getLoginStatus = () => rawGet('login/status');

const isLoggedIn = route =>
  getLoginStatus().then(res => {
    if (res.loggedIn) {
      return res;
    }
    throw new CogniteSDKError(
      `Can not access ${route}, you are not logged in.`,
      403
    );
  });

function get(url, { params } = {}, middleware = false) {
  if (middleware) {
    return isLoggedIn(url).then(() => rawGet(url, params));
  }
  return rawGet(url, params);
}

function rawPost(url, data) {
  return instance({
    method: 'post',
    url: `${options.baseUrl}/${url}`,
    data,
    withCredentials: options.withCredentials,
  }).then(retrieveData);
}

function post(url, body, middleware = true) {
  if (middleware) {
    return isLoggedIn().then(() => rawPost(url, body));
  }
  return rawPost(url, body);
}

function rawPut(url, data) {
  return instance({
    method: 'put',
    url: `${options.baseUrl}/${url}`,
    data,
    withCredentials: options.withCredentials,
  }).then(retrieveData);
}

function put(url, body, middleware = true) {
  if (middleware) {
    return isLoggedIn().then(() => rawPut(url, body));
  }
  return rawPut(url, body);
}

function stringifyIfNeeded(param) {
  return typeof param === 'string' ? param : JSON.stringify(param);
}

function search(url, { assetIds, assetSubtrees, metadata, ...params } = {}) {
  return get(url, {
    params: {
      ...params,
      assetIds: stringifyIfNeeded(assetIds),
      assetSubtrees: stringifyIfNeeded(assetSubtrees),
      metadata: stringifyIfNeeded(metadata),
    },
  });
}

function getLoginUrl({
  project = options.project,
  redirectUrl = window.location.href,
  errorRedirectUrl = window.location.href,
}) {
  return get(
    `login/url`,
    {
      params: {
        project,
        redirectUrl,
        errorRedirectUrl,
      },
    },
    false
  );
}

function setBearerToken(token) {
  instance.defaults.headers.Authorization = `Bearer ${token}`;
}

// Pass in url to an empty html file on the same domain as your application
async function getAccessTokenFromSession(silentRedirectURL) {
  if (silentRedirectURL === null) {
    throw new Error(
      'No silent redirect url specified. Please pass it as an argument'
    );
  }

  // Prevent possible recursiveness
  if (window != null && window.self === window.top) {
    const loginUrl = (await getLoginUrl({
      redirectUrl: silentRedirectURL,
      errorRedirectUrl: silentRedirectURL,
    })).url;

    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.name = 'cognite-auth-frame';
      iframe.style = 'width:0;height:0;border:0;border:none;';
      iframe.onload = () => {
        try {
          const qs = queryString.parse(iframe.contentWindow.location.search);
          if (qs.error == null && qs.access_token != null) {
            resolve(qs.access_token);
          } else {
            reject(new CogniteSDKError(`${qs.error}: ${qs.error_description}`));
          }
        } catch (e) {
          reject(e);
        } finally {
          document.body.removeChild(iframe);
        }
      };
      iframe.src = `${loginUrl}&prompt=none`;
      document.body.appendChild(iframe);
    });
  }
  return null;
}

function configure(opts) {
  options = Object.assign({}, options, opts);
  if (options.apiKey) {
    instance.defaults.headers['Api-Key'] = options.apiKey;
  }

  return options;
}

function loginWithRedirect({
  project = options.project,
  redirectUrl = window.location.href,
  errorRedirectUrl = window.location.href,
} = {}) {
  return getLoginUrl({ project, redirectUrl, errorRedirectUrl }).then(data => {
    window.location.href = data.url;
  });
}

function loginWithApiKey(apiKey) {
  instance.defaults.headers['Api-Key'] = apiKey;
  return isLoggedIn().catch(() => {
    delete instance.defaults.headers['Api-Key'];
    throw new CogniteSDKError(`Could not authenticate.`, 400);
  });
}

function tagMatching({
  project = options.project,
  platform = 'IA',
  fuzzyThreshold = 0,
  tagIds = [],
}) {
  const body = {
    tagIds,
    metadata: {
      fuzzyThreshold,
      platform,
    },
  };
  return post(`${apiUrl()}projects/${project}/tagmatching/`, body);
}

function unsetApiKey() {
  options = Object.assign({}, options, {
    apiKey: null,
  });
  delete instance.defaults.headers['Api-Key'];
}

function logout({ redirectUrl = null } = {}) {
  return get('logout/url', {
    params: { redirectUrl },
  }).then(res => {
    unsetApiKey();
    options = initialOptions;
    return res.url;
  });
}

function getAsset({ project = options.project, assetId } = {}) {
  return get(`${apiUrl()}projects/${project}/assets/${assetId}`);
}

function getAssetSubtree({
  project = options.project,
  assetId,
  ...params
} = {}) {
  return get(`${apiUrl()}projects/${project}/assets/${assetId}/subtree`, {
    params,
  });
}

function getAssets({ project = options.project, ...params } = {}) {
  return get(`${apiUrl()}projects/${project}/assets/`, { params });
}

function searchAssets({ project = options.project, ...params }) {
  return search(`${apiUrl()}projects/${project}/assets/search`, params);
}

function getProject({ project = options.project } = {}) {
  return get(`${apiUrl()}projects/${project}`);
}

function getEvents({ project = options.project, ...params } = {}) {
  return get(`${apiUrl()}projects/${project}/events`, { params });
}

function getEvent({ project = options.project, id } = {}) {
  return get(`${apiUrl()}projects/${project}/events/${id}`);
}

function searchEvents({ project = options.project, ...params }) {
  return search(`${apiUrl()}projects/${project}/events/search`, params);
}

function createEvents({ project = options.project, body }) {
  return post(
    `${apiUrl()}projects/${project}/events`,
    body,
    true,
    result => result
  );
}

function updateEvents({ project = options.project, body }) {
  return post(
    `${apiUrl()}projects/${project}/events/update`,
    body,
    true,
    result => result
  );
}

function getTimeSeries({ project = options.project, ...params } = {}) {
  return get(`${apiUrl()}projects/${project}/timeseries`, { params });
}

function getDatapoints({ project = options.project, name, ...params } = {}) {
  return get(
    `${apiUrl()}projects/${project}/timeseries/data/${encodeURIComponent(
      name
    )}`,
    { params },
    false
  );
}

function getDatapointsFrame({ project = options.project, body } = {}) {
  return post(
    `${apiUrl()}projects/${project}/timeseries/dataframe`,
    body,
    true,
    result => result
  );
}

function getLatestDataPoint({
  project = options.project,
  name,
  ...params
} = {}) {
  return get(
    `${apiUrl()}projects/${project}/timeseries/latest/${encodeURIComponent(
      name
    )}`,
    { params }
  );
}

function getFiles({ project = options.project, ...params } = {}) {
  return get(`${apiUrl()}projects/${project}/files`, { params });
}

function searchFiles({ project = options.project, ...params }) {
  return search(`${apiUrl()}projects/${project}/files/search`, params);
}

function getFile({ project = options.project, id } = {}) {
  return get(`${apiUrl()}projects/${project}/files/${id}`);
}

function getDownloadLink({ project = options.project, id } = {}) {
  return get(`${apiUrl()}projects/${project}/files/${id}/downloadlink`);
}

function timeseriesSearch({ project = options.project, q, asset, ...rest }) {
  const qParams = { q, path: [asset], ...rest };
  const dParams = { description: q, path: [asset], ...rest };
  return Promise.join(
    get(
      `${apiUrl()}projects/${project}/timeseries`,
      {
        params: qParams,
      },
      false
    ),
    get(
      `${apiUrl()}projects/${project}/timeseries`,
      {
        params: dParams,
      },
      false
    )
  ).then(([a, b]) => sortedUniqBy([...a.items, ...b.items], i => i.tagId));
}

function searchTimeSeries({ project = options.project, ...params }) {
  return search(`${apiUrl()}projects/${project}/timeseries/search`, params);
}

function getTablesInDatabase({
  apiKey = options.apiKey,
  project = options.project,
  dbName,
}) {
  instance.defaults.headers['Api-Key'] = apiKey;
  return get(`${apiUrl()}projects/${project}/raw/${dbName}`);
}

function createTable({
  apiKey = options.apiKey,
  project = options.project,
  table,
  dbName,
}) {
  instance.defaults.headers['Api-Key'] = apiKey;
  const body = {
    items: [{ tableName: table }],
  };
  return post(`${apiUrl()}projects/${project}/raw/${dbName}/create`, body);
}

module.exports = {
  loginWithRedirect,
  getLoginStatus,
  configure,
  logout,
  getAssets,
  getAssetSubtree,
  getAsset,
  searchAssets,
  loginWithApiKey,
  unsetApiKey,
  getProject,
  getEvents,
  getEvent,
  searchEvents,
  createEvents,
  updateEvents,
  getLatestDataPoint,
  getTimeSeries,
  getDatapointsFrame,
  get,
  post,
  put,
  getDatapoints,
  tagMatching,
  getFiles,
  getFile,
  searchFiles,
  getDownloadLink,
  timeseriesSearch,
  searchTimeSeries,
  getAccessTokenFromSession,
  setBearerToken,
  getTablesInDatabase,
  createTable,
  instance,
};
